<!--
Component Notes:
Uses https://flatpickr.js.org

Incoming model is DatePickerObj (see InputUtils.js)
Date formats - see https://flatpickr.js.org/formatting/

Set "id" prop when possible, otherwise a randomly generated ID will be set.

When flatpickr is defined with altInput=true (which this is), it will create a second input element
  while the original input element will be hidden. This means any changes to the class via
   calculatedClasses(), will be applied to the hidden input and not synced to the visible input.
   A MutationObserver will monitor the hidden input "class" and update the visible input with
   the hidden input class list;


-->

<template>
	<div class="mb-4">
		<label v-if="props.label" class="mb-2 block leading-6 text-label" :class="props.labelTextSize">
			{{ props.label }}
		</label>
		<div class="relative group">
			<!-- Model is expected to be a DatePickerObj -->
			<flat-pickr
				:id="props.id"
				v-model="model.date"
				:config="config"
				:class="calculatedClasses()"
				:placeholder="props.placeholder"
				:disabled="props.disabled"
				@onChange="dateSelected"
				@onClose="onClose"
				data-class="flatpickr-right"
			/>

			<div v-show="!props.disabled" class="pointer-events-none absolute inset-0 right-5 left-auto flex items-center">
				<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" class="group-hover:text-primary-hover fill-current dark:fill-icon-primary-on-color">
					<title>Edit</title>
					<path fill-rule="evenodd" clip-rule="evenodd" d="M1.56622 3.23223C2.03506 2.76339 2.67094 2.5 3.33398 2.5H9.16732C9.62755 2.5 10.0006 2.8731 10.0006 3.33333C10.0006 3.79357 9.62755 4.16667 9.16732 4.16667H3.33398C3.11297 4.16667 2.90101 4.25446 2.74473 4.41074C2.58845 4.56702 2.50065 4.77899 2.50065 5V16.6667C2.50065 16.8877 2.58845 17.0996 2.74473 17.2559C2.90101 17.4122 3.11297 17.5 3.33398 17.5H15.0006C15.2217 17.5 15.4336 17.4122 15.5899 17.2559C15.7462 17.0996 15.834 16.8877 15.834 16.6667V10.8333C15.834 10.3731 16.2071 10 16.6673 10C17.1276 10 17.5006 10.3731 17.5006 10.8333V16.6667C17.5006 17.3297 17.2373 17.9656 16.7684 18.4344C16.2996 18.9033 15.6637 19.1667 15.0006 19.1667H3.33398C2.67094 19.1667 2.03506 18.9033 1.56622 18.4344C1.09738 17.9656 0.833984 17.3297 0.833984 16.6667V5C0.833984 4.33696 1.09738 3.70107 1.56622 3.23223Z" />
					<path fill-rule="evenodd" clip-rule="evenodd" d="M16.6673 2.39909C16.4195 2.39909 16.1818 2.49754 16.0066 2.67278L8.25314 10.4262L7.81264 12.1882L9.57463 11.7477L17.3281 3.99427C17.5033 3.81903 17.6018 3.58135 17.6018 3.33352C17.6018 3.0857 17.5033 2.84802 17.3281 2.67278C17.1528 2.49754 16.9152 2.39909 16.6673 2.39909ZM14.8281 1.49427C15.3159 1.00647 15.9775 0.732422 16.6673 0.732422C17.3572 0.732422 18.0188 1.00647 18.5066 1.49427C18.9944 1.98207 19.2684 2.64367 19.2684 3.33352C19.2684 4.02338 18.9944 4.68498 18.5066 5.17278L10.5899 13.0894C10.4831 13.1962 10.3493 13.272 10.2028 13.3086L6.86945 14.142C6.58547 14.213 6.28506 14.1298 6.07808 13.9228C5.8711 13.7158 5.78789 13.4154 5.85888 13.1314L6.69222 9.79808C6.72885 9.65155 6.80461 9.51773 6.91141 9.41093L14.8281 1.49427Z" />
				</svg>
			</div>
		</div>
		<ValidationError :errors="props.errors" :field="props.errorField" class="mt-2 ml-3.5" />
	</div>
</template>

<script setup lang="js">
import { onMounted, onUnmounted, ref } from 'vue'
import { defineModel } from 'vue'
import ValidationError from '@/components/Forms/InputError.vue'
import { ApiGatewayResponse } from '@/api/ApiGateway.js'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { DatePickerObj } from '@/models/utils/InputUtils.js'

const props = defineProps({
	//If you don't pass in an ID and target by class, then styles cannot be applied after rendered (it creates a second input in the background)
	//  Random id is generated if no ID is passed in (in cases like the table filters
	id: { type: String, default: Math.random().toString(36).replace('0.', 'date_picker_' || '') },
	placeholder: { type: String, default: 'YYYY-MM-DD' },
	customClasses: { type: String, default: null },
	label: { type: String, default: null },
	labelTextSize: { type: String, default: null },
	inputBgColor: { type: String, default: 'bg-neutral' },
	errors: { type: ApiGatewayResponse, default: null },
	errorField: { type: String, default: null },
	disabled: { type: Boolean, default: false }
})

//Incoming DatePickerObj
const model = defineModel({
	required: true,
	default: new DatePickerObj('', {
		dateFormat: 'Y-m-d',
		altFormat: 'm-d-Y',
		noCalendar: false,
		enableTime: false,
		time_24hr: false,
		minuteIncrement: 1,
	})
})

//Config for date component (https://flatpickr.js.org/options/)
const config = ref({
	//wrap: true, // set wrap to true only when using 'input-group'
	altFormat: model.value.config.altFormat,//display format
	altInput: true,
	dateFormat: model.value.config.dateFormat, //database format
	enableTime: model.value.config.enableTime,
	noCalendar: model.value.config.noCalendar,
	time_24hr: model.value.config.time_24hr,
	minuteIncrement: model.value.config.minuteIncrement,
})

const emit = defineEmits(['dateSelected', 'onClose'])

/**
 * Create input field class based on properties
 * @returns {string}
 */
function calculatedClasses()
{
	//[, props.customClasses]
	const classes = [
		props.id,
		'datepicker',
		'w-full h-10 p-4 rounded outline-none group-hover:border-primary-bold-hover group-hover:ring-1 group-hover:ring-primary-bold-hover focus:ring-1 focus:ring-neutral-subtle focus:bg-neutral focus:border-neutral-subtle placeholder-default dark:border-tertiary-dark dark:bg-primary-dark dark:focus:border-primary',
		props.customClasses ? props.customClasses : '',
		props.disabled ? 'text-disabled' : 'text-label dark:text-label-on-color dark:placeholder-on-color',
		props.inputBgColor,
		hasError() ? 'border-error' : 'border-neutral'
	]
	return classes.join(' ')
}

let mutationObserver = null //Keeps input class updated with hidden input

onMounted(() =>
{
	// When flatpickr is defined with altInput=true, it will create a second input
	// element while the original input element will be hidden. With MutationObserver
	// we can copy classnames from hidden to the visible element.
	//https://github.com/flatpickr/flatpickr/issues/2428#issuecomment-881257341
	try
	{
		const hidden_input = document.getElementById(props.id)

		if (hidden_input && hidden_input.type === 'hidden')
		{
			mutationObserver = new MutationObserver(mutationObserverCallback)
			mutationObserver.observe(hidden_input, { attributes: true })
		}
	}
	catch (error)
	{
		console.error('Error mounting date picker', error)
	}
})

onUnmounted(() =>
{
	try
	{
		//Cleanup observer
		if (mutationObserver && mutationObserver.disconnect)
		{
			mutationObserver.disconnect()
		}
	}
	catch (error)
	{
		console.error('Error unmounting date picker', error)
	}
})

/**
 * Using altInput=true creates a hidden input for the original value in DatePickerObj.db_format
 * and a secondary input that is used to display the date using the DatePickerObj.display_format.
 * Monitor the hidden input for changes to the class to update the visible input. This allows us
 * to put a red border around the field when validation fails.
 * @param mutationsList
 */
function mutationObserverCallback(mutationsList)
{
	mutationsList.forEach((mutation) =>
	{
		//If hidden input "class" has changed, update visible input
		if (mutation.attributeName === 'class')
		{
			//The id is added to the class of both elements
			const pickers = document.getElementsByClassName(props.id)
			if (pickers && pickers.length === 2)
			{
				const hidden_picker = pickers[0].type === 'hidden' ? pickers[0] : pickers[1]
				const picker = pickers[1].type === 'text' ? pickers[1] : pickers[0]
				//console.log('hidden_picker', hidden_picker.classList)
				//console.log('picker', picker.classList)
				if (hidden_picker && picker)
				{
					picker.classList = hidden_picker.classList
				}
			}
		}
	})
}

function hasError()
{
	return (
		props.errors?.validationErrors()?.find((error) => error.source === props.errorField) != null
	)
}

/**
 * Called when a date is changed but before the underlying model is updated
 * @param date
 */
function dateSelected(date) {
	emit('dateSelected', date, props.id)
}

/**
 * Called when the calendar is closed and after the value is set.
 * @param date
 */
function onClose(date) {
	emit('onClose', date, props.id)
}
</script>

<style scoped>
input::placeholder {
	color: #64748b;
	opacity: 0.5;
	font-weight: normal;
}
</style>